import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DatabaseBackup, Download, Trash2, UploadCloud, RotateCcw, Save, PlayCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { adminApi } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';

const formatBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const CollectionPicker = ({ collections, collectionNames, selected, onToggle, testidPrefix }) => (
  <div className="border border-gray-200 rounded-md p-3 max-h-56 overflow-y-auto bg-gray-50">
    <label className="flex items-center gap-2 py-1 text-sm font-medium">
      <Checkbox
        checked={selected.includes('*')}
        onCheckedChange={() => onToggle('*')}
        data-testid={`${testidPrefix}-all`}
      />
      <span>All collections ({collectionNames.length})</span>
    </label>
    <div className="grid grid-cols-2 gap-1 mt-1">
      {collections.map((c) => (
        <label key={c.name} className="flex items-center gap-2 py-0.5 text-xs">
          <Checkbox
            checked={!selected.includes('*') && selected.includes(c.name)}
            onCheckedChange={() => onToggle(c.name)}
            disabled={selected.includes('*')}
            data-testid={`${testidPrefix}-${c.name}`}
          />
          <span className="truncate">{c.name}</span>
          <span className="text-gray-400 ml-auto">{c.count ?? '—'}</span>
        </label>
      ))}
    </div>
  </div>
);

const AdminBackupsPanel = () => {
  const { toast } = useToast();
  const [collections, setCollections] = useState([]);            // [{name, count}]
  const [backups, setBackups] = useState([]);
  const [schedule, setSchedule] = useState({ enabled: false, time: '02:00', collections: ['*'], retention: 7 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickedManual, setPickedManual] = useState(['*']);       // selection for "Backup Now"
  const [pickedSchedule, setPickedSchedule] = useState(['*']);   // selection for daily schedule
  const [restoreMode, setRestoreMode] = useState('replace');
  const restoreFileRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b, s] = await Promise.all([
        adminApi.listBackupCollections(),
        adminApi.listBackups(),
        adminApi.getBackupSchedule(),
      ]);
      setCollections(c.collections || []);
      setBackups(b || []);
      setSchedule({
        enabled: !!s.enabled, time: s.time || '02:00',
        collections: s.collections?.length ? s.collections : ['*'],
        retention: s.retention || 7,
        last_run_at: s.last_run_at, last_status: s.last_status,
      });
      setPickedSchedule(s.collections?.length ? s.collections : ['*']);
    } catch (err) {
      toast({ title: 'Failed to load', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const collectionNames = useMemo(() => collections.map((c) => c.name), [collections]);

  const toggleInSelection = (setSel, sel, name) => {
    if (name === '*') { setSel(['*']); return; }
    const isAll = sel.includes('*');
    const next = isAll ? [name] : sel.includes(name) ? sel.filter((n) => n !== name) : [...sel, name];
    setSel(next.length ? next : ['*']);
  };

  const runBackup = async () => {
    setBusy(true);
    try {
      const r = await adminApi.createBackup(pickedManual);
      toast({ title: 'Backup created', description: `${r.total_docs} docs across ${r.collections.length} collections` });
      await loadAll();
    } catch (err) {
      toast({ title: 'Backup failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const onDownload = (id) => {
    // Browsers can't read httpOnly cookies, but the GET will send the admin cookie.
    window.open(adminApi.backupDownloadUrl(id), '_blank');
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this backup permanently?')) return;
    try {
      await adminApi.deleteBackup(id);
      toast({ title: 'Backup deleted' });
      setBackups((bs) => bs.filter((b) => b.id !== id));
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const onSaveSchedule = async () => {
    setBusy(true);
    try {
      const payload = {
        enabled: !!schedule.enabled,
        time: schedule.time || '02:00',
        collections: pickedSchedule,
        retention: Number(schedule.retention) || 7,
      };
      const s = await adminApi.saveBackupSchedule(payload);
      setSchedule({ ...s, collections: s.collections || ['*'] });
      toast({ title: 'Schedule saved', description: s.enabled ? `Auto-backup at ${s.time} UTC daily` : 'Auto-backup disabled' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const onRestore = async () => {
    const f = restoreFileRef.current?.files?.[0];
    if (!f) { toast({ title: 'Pick a .zip backup first', variant: 'destructive' }); return; }
    const msg = restoreMode === 'replace'
      ? 'REPLACE mode will WIPE every collection in this ZIP and reinsert the data. Continue?'
      : 'MERGE mode will upsert documents by `id`. Continue?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await adminApi.restoreBackup(f, restoreMode);
      const total = Object.keys(r.report || {}).length;
      toast({ title: 'Restore complete', description: `Touched ${total} collections.` });
      restoreFileRef.current.value = '';
    } catch (err) {
      toast({ title: 'Restore failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500" data-testid="backups-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading backups…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backups-panel">
      <div className="flex items-center gap-3">
        <DatabaseBackup className="w-6 h-6 text-[#ee5e23]" />
        <div>
          <h1 className="text-xl font-bold">Backups</h1>
          <p className="text-sm text-gray-500">Export selected collections as a downloadable ZIP. Schedule daily auto-backups. Restore from a previous ZIP.</p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={loadAll} data-testid="backups-refresh">
          <RotateCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Manual backup */}
      <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold text-gray-800">Backup Now</h2>
        <p className="text-xs text-gray-500">Pick which collections to include, then create a one-off backup. The ZIP becomes downloadable from the list below.</p>
        <CollectionPicker
          collections={collections}
          collectionNames={collectionNames}
          selected={pickedManual}
          onToggle={(n) => toggleInSelection(setPickedManual, pickedManual, n)}
          testidPrefix="manual-col"
        />
        <Button onClick={runBackup} disabled={busy} data-testid="backup-now-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
          Backup Now
        </Button>
      </section>

      {/* Schedule */}
      <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold text-gray-800">Daily Auto-Backup Schedule</h2>
        <p className="text-xs text-gray-500">Backups run at the configured UTC time and keep the latest N auto-backups. Manual backups are never auto-deleted.</p>

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Switch
              checked={!!schedule.enabled}
              onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })}
              data-testid="schedule-enabled"
            />
            {schedule.enabled ? 'Enabled' : 'Disabled'}
          </label>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Time (UTC, HH:MM)</Label>
            <Input
              type="time"
              className="w-32"
              value={schedule.time || '02:00'}
              onChange={(e) => setSchedule({ ...schedule, time: e.target.value })}
              data-testid="schedule-time"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Keep last N</Label>
            <Input
              type="number"
              min="1"
              max="365"
              className="w-20"
              value={schedule.retention || 7}
              onChange={(e) => setSchedule({ ...schedule, retention: e.target.value })}
              data-testid="schedule-retention"
            />
          </div>
        </div>

        <CollectionPicker
          collections={collections}
          collectionNames={collectionNames}
          selected={pickedSchedule}
          onToggle={(n) => toggleInSelection(setPickedSchedule, pickedSchedule, n)}
          testidPrefix="schedule-col"
        />

        {schedule.last_run_at && (
          <div className="text-xs text-gray-500">
            Last run: <span className="font-mono">{formatDate(schedule.last_run_at)}</span> — {schedule.last_status || '—'}
          </div>
        )}

        <Button onClick={onSaveSchedule} disabled={busy} data-testid="schedule-save">
          <Save className="w-4 h-4 mr-1" /> Save Schedule
        </Button>
      </section>

      {/* Backup list */}
      <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold text-gray-800">Saved Backups ({backups.length})</h2>
        {backups.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">No backups yet. Use “Backup Now” above to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="backups-table">
              <thead className="text-left text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Kind</th>
                  <th className="py-2 pr-4">Collections</th>
                  <th className="py-2 pr-4">Docs</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs">{formatDate(b.created_at)}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs ${b.kind === 'auto' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>{b.kind}</span>
                    </td>
                    <td className="py-2 pr-4 max-w-xs truncate" title={(b.collections || []).join(', ')}>
                      {(b.collections || []).length === collectionNames.length ? 'All' : (b.collections || []).join(', ')}
                    </td>
                    <td className="py-2 pr-4">{b.total_docs}</td>
                    <td className="py-2 pr-4">{formatBytes(b.size_bytes)}</td>
                    <td className="py-2 pr-4 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => onDownload(b.id)} data-testid={`backup-download-${b.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Download
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDelete(b.id)} data-testid={`backup-delete-${b.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Restore */}
      <section className="bg-white border border-amber-200 rounded-lg p-5 space-y-3 bg-amber-50/30">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          <UploadCloud className="w-5 h-5 text-amber-600" /> Restore from Backup
        </h2>
        <p className="text-xs text-amber-700">
          <b>Caution:</b> Replace mode wipes existing data in the included collections before re-inserting. Always run a fresh backup first.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <Input type="file" accept=".zip" ref={restoreFileRef} className="max-w-sm" data-testid="restore-file" />
          <select
            className="border border-gray-300 rounded px-2 py-2 text-sm bg-white"
            value={restoreMode}
            onChange={(e) => setRestoreMode(e.target.value)}
            data-testid="restore-mode"
          >
            <option value="replace">Replace (wipe & reinsert)</option>
            <option value="merge">Merge (upsert by id)</option>
          </select>
          <Button onClick={onRestore} disabled={busy} variant="destructive" data-testid="restore-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1" />}
            Restore
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AdminBackupsPanel;
